import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Building2, Settings, CheckCircle, XCircle, Shield, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TenantModulesManagement({ user }) {
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [availableModules, setAvailableModules] = useState({});
  const [baseModules, setBaseModules] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantModules, setTenantModules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, modulesRes, overviewRes] = await Promise.all([
        axios.get(`${API}/super-admin/tenants`),
        axios.get(`${API}/super-admin/modules/available`),
        axios.get(`${API}/super-admin/tenants-modules-overview`)
      ]);

      setTenants(overviewRes.data.tenants || []);
      setAvailableModules(modulesRes.data.modules || {});
      setBaseModules(modulesRes.data.base_modules || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const openModulesDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/super-admin/tenants/${tenant.tenant_id}/modules`);
      setTenantModules(response.data.enabled_modules || []);
    } catch (error) {
      console.error('Error fetching tenant modules:', error);
      toast.error('Erreur lors du chargement des modules');
    }
  };

  const toggleModule = (moduleKey) => {
    if (baseModules.includes(moduleKey)) {
      toast.error('Les modules de base ne peuvent pas être désactivés');
      return;
    }

    setTenantModules(prev => {
      if (prev.includes(moduleKey)) {
        return prev.filter(m => m !== moduleKey);
      } else {
        return [...prev, moduleKey];
      }
    });
  };

  const saveModules = async () => {
    if (!selectedTenant) return;

    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/tenants/${selectedTenant.tenant_id}/modules`, {
        enabled_modules: tenantModules
      });

      toast.success('Modules mis à jour avec succès');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving modules:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getModulesByCategory = (category) => {
    return Object.entries(availableModules)
      .filter(([key, val]) => val.category === category)
      .map(([key, val]) => ({ key, ...val }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Gestion des modules par établissement
          </CardTitle>
          <CardDescription>
            Activez ou désactivez des modules pour chaque établissement
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Liste des tenants */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {tenants.map((tenant) => (
          <Card key={tenant.tenant_id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">{tenant.tenant_name}</h3>
                </div>
                <Badge variant="outline">
                  {tenant.total_modules} modules
                </Badge>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Modules de base</span>
                  <Badge className="bg-green-100 text-green-800">
                    {tenant.base_modules}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Modules avancés</span>
                  <Badge className="bg-blue-100 text-blue-800">
                    {tenant.advanced_modules}
                  </Badge>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={() => openModulesDialog(tenant)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Gérer les modules
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog de gestion des modules */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Modules pour {selectedTenant?.tenant_name}
            </DialogTitle>
            <DialogDescription>
              Activez ou désactivez les modules disponibles pour cet établissement
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Modules de base */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-lg">Modules de base (toujours activés)</h3>
              </div>
              <div className="grid gap-3">
                {getModulesByCategory('base').map((module) => (
                  <Card key={module.key} className="border-green-200 bg-green-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-900">{module.name}</h4>
                          <p className="text-sm text-green-700">{module.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <Lock className="w-4 h-4 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Modules avancés */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Settings className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-lg">Modules avancés (optionnels)</h3>
              </div>
              <div className="grid gap-3">
                {getModulesByCategory('advanced').map((module) => {
                  const isEnabled = tenantModules.includes(module.key);
                  return (
                    <Card 
                      key={module.key} 
                      className={`cursor-pointer transition-all ${
                        isEnabled ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                      }`}
                      onClick={() => toggleModule(module.key)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{module.name}</h4>
                            <p className="text-sm text-gray-600">{module.description}</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            {isEnabled ? (
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-gray-400" />
                            )}
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => toggleModule(module.key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={saveModules}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer les modules'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TenantModulesManagement;
